import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Shield, AlertTriangle, CheckCircle, Clock, Bell, BellOff,
  Globe, Plus, Trash2, ChevronRight, X, User, Star,
  BarChart2, List, Settings, RefreshCw
} from "lucide-react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";

const API = '/api';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const normalizeDomain = (d) =>
  d.toLowerCase().replace(/^https?:\/\//, "").replace(/^www\./, "").split("/")[0].trim();

const formatDate = (iso) => {
  if (!iso) return "Never";
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short", day: "numeric", hour: "numeric", minute: "2-digit"
    });
  } catch { return iso; }
};

const CATEGORY_LABELS = {
  financial_scam: "Financial scam",
  tech_support_scam: "Tech support scam",
  blacklisted_site: "Flagged site visited",
  phishing: "Phishing attempt",
  other: "Risk event"
};

const RISK_COLORS = {
  DANGER: { color: "#ef4444", bg: "rgba(239,68,68,0.12)", label: "High Risk" },
  CAUTION_HARD: { color: "#f59e0b", bg: "rgba(245,158,11,0.12)", label: "Caution" }
};

// Web push — the VAPID *public* key (safe to embed; the private key lives
// only in Railway env). Must match VAPID_PRIVATE_KEY on the server.
const VAPID_PUBLIC_KEY = "BCj52DkjB-N5sq3TH9_RKXOlChWMCKDAgmQgYVLqzQjglVXj8WSbtAyEJyllY-qiGopvYL9VPXwoGu8j8kzIr5Q";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

// ─── Sub-components ────────────────────────────────────────────────────────────

function StatCard({ label, value, color, icon: Icon }) {
  return (
    <div className="cp-stat-card">
      <div className="cp-stat-icon" style={{ color }}><Icon size={20} /></div>
      <div className="cp-stat-value" style={{ color }}>{value}</div>
      <div className="cp-stat-label">{label}</div>
    </div>
  );
}

function WeeklyChart({ data }) {
  if (!data?.length) return null;
  const maxVal = Math.max(...data.map(d => d.danger + d.caution), 1);
  return (
    <div className="cp-chart">
      {data.map((week, i) => (
        <div key={i} className="cp-chart-col">
          <div className="cp-chart-bars">
            <div
              className="cp-chart-bar cp-chart-bar--danger"
              style={{ height: `${(week.danger / maxVal) * 60}px` }}
              title={`${week.danger} danger`}
            />
            <div
              className="cp-chart-bar cp-chart-bar--caution"
              style={{ height: `${(week.caution / maxVal) * 60}px` }}
              title={`${week.caution} caution`}
            />
          </div>
          <span className="cp-chart-label">{week.label.replace(" ago", "").replace("Last ", "")}</span>
        </div>
      ))}
      <div className="cp-chart-legend">
        <span className="cp-legend-dot" style={{ background: "#ef4444" }} /> High risk
        <span className="cp-legend-dot" style={{ background: "#f59e0b", marginLeft: 12 }} /> Caution
      </div>
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────

export default function CaregiverPortalPage() {
  // ── Link-based family view (new pairing system) ──
  const { user, session: supaSession } = useAuth();
  const [familyView, setFamilyView] = useState({ loading: false, seniors: null, error: null });

  const loadFamily = useCallback(async () => {
    const token = supaSession?.access_token;
    if (!token) return;
    setFamilyView(prev => ({ ...prev, loading: true, error: null }));
    try {
      const res = await axios.get(`${API}/caregiver/my-dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data?.ok) {
        setFamilyView({ loading: false, seniors: res.data.seniors || [], error: null });
      } else {
        setFamilyView({ loading: false, seniors: [], error: res.data?.error || null });
      }
    } catch {
      setFamilyView({ loading: false, seniors: [], error: "network" });
    }
  }, [supaSession]);

  useEffect(() => {
    if (user && supaSession?.access_token) loadFamily();
  }, [user, supaSession, loadFamily]);

  // ── Phone alerts (web push) ──
  const [pushState, setPushState] = useState("idle"); // idle|enabled|denied|unsupported|error
  const [pushError, setPushError] = useState("");

  const enablePush = async () => {
    setPushError("");
    try {
      if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) {
        setPushState("unsupported");
        return;
      }
      const perm = await Notification.requestPermission();
      if (perm !== "granted") { setPushState("denied"); return; }

      const reg = await navigator.serviceWorker.register("/sw.js");
      await navigator.serviceWorker.ready;

      // A subscription created with a different application server key
      // makes subscribe() throw InvalidStateError. Drop the old one first
      // so re-subscribing after a key change just works.
      const existing = await reg.pushManager.getSubscription();
      if (existing) {
        try { await existing.unsubscribe(); } catch (_) {}
      }

      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });

      const res = await axios.post(
        `${API}/caregiver/push-subscribe`,
        { subscription: sub.toJSON() },
        { headers: { Authorization: `Bearer ${supaSession.access_token}` } }
      );
      if (res.data?.ok) {
        setPushState("enabled");
      } else {
        setPushState("error");
        setPushError(res.data?.error || "server rejected the subscription");
      }
    } catch (e) {
      setPushState("error");
      setPushError(e?.name ? `${e.name}: ${e.message || ""}`.trim() : String(e));
    }
  };

  // ── Auth state (legacy access-code system) ──
  const [codeInput, setCodeInput] = useState("");
  const [authError, setAuthError] = useState("");
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [session, setSession] = useState(() => {
    try {
      const s = sessionStorage.getItem("cp_session");
      return s ? JSON.parse(s) : null;
    } catch { return null; }
  });

  // ── Dashboard state ──
  const [activeTab, setActiveTab] = useState("overview"); // overview | reminders | sites | activity
  const [dashboard, setDashboard] = useState(null);
  const [reminders, setReminders] = useState([]);
  const [siteList, setSiteList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // ── Reminder form ──
  const [showReminderForm, setShowReminderForm] = useState(false);
  const [reminderForm, setReminderForm] = useState({
    title: "", body: "", repeat: "none", scheduled_time: "", scheduled_weekday: ""
  });

  // ── Site list form ──
  const [showSiteForm, setShowSiteForm] = useState(false);
  const [siteForm, setSiteForm] = useState({ domain: "", list_type: "blacklist", note: "" });
  const [siteFormError, setSiteFormError] = useState("");

  // ── Load data ──
  const loadDashboard = useCallback(async (s) => {
    if (!s) return;
    setIsLoading(true);
    try {
      const [dashRes, remRes, siteRes] = await Promise.all([
        axios.get(`${API}/caregiver/dashboard/${s.user_id}?code=${s.code}`),
        axios.get(`${API}/caregiver/reminders/${s.user_id}?code=${s.code}`),
        axios.get(`${API}/caregiver/site-list/${s.user_id}?code=${s.code}`)
      ]);
      setDashboard(dashRes.data);
      setReminders(remRes.data || []);
      setSiteList(siteRes.data || []);
    } catch (err) {
      if (err.response?.status === 401) {
        sessionStorage.removeItem("cp_session");
        setSession(null);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (session) loadDashboard(session);
  }, [session, loadDashboard]);

  // ── Auth ──
  const handleAuth = async () => {
    const code = codeInput.trim().toUpperCase();
    if (!code) return;
    setIsAuthenticating(true);
    setAuthError("");
    try {
      const res = await axios.post(`${API}/caregiver/auth`, { code });
      const s = { user_id: res.data.user_id, code };
      sessionStorage.setItem("cp_session", JSON.stringify(s));
      setSession(s);
    } catch (err) {
      setAuthError(err.response?.status === 401
        ? "Invalid access code. Check with the person who shared it."
        : "Something went wrong. Please try again.");
    } finally {
      setIsAuthenticating(false);
    }
  };

  // ── Reminders ──
  const handleCreateReminder = async () => {
    if (!reminderForm.title.trim() || !reminderForm.body.trim()) return;
    try {
      await axios.post(`${API}/caregiver/reminders`, {
        user_id: session.user_id,
        caregiver_contact_id: "portal",
        caregiver_name: "Your caregiver",
        title: reminderForm.title.trim(),
        body: reminderForm.body.trim(),
        repeat: reminderForm.repeat,
        scheduled_time: reminderForm.scheduled_time || null,
        scheduled_weekday: reminderForm.scheduled_weekday !== "" ? parseInt(reminderForm.scheduled_weekday) : null,
      });
      setReminderForm({ title: "", body: "", repeat: "none", scheduled_time: "", scheduled_weekday: "" });
      setShowReminderForm(false);
      await loadDashboard(session);
    } catch (err) {
      console.error("[REMINDER] Create error", err);
    }
  };

  const handleDeleteReminder = async (id) => {
    await axios.delete(`${API}/caregiver/reminders/${id}`);
    setReminders(prev => prev.filter(r => r.id !== id));
  };

  // ── Site list ──
  const handleAddSite = async () => {
    setSiteFormError("");
    if (!siteForm.domain.trim()) { setSiteFormError("Domain required"); return; }
    try {
      await axios.post(`${API}/caregiver/site-list`, {
        user_id: session.user_id,
        caregiver_contact_id: "portal",
        caregiver_name: "Your caregiver",
        domain: normalizeDomain(siteForm.domain),
        list_type: siteForm.list_type,
        note: siteForm.note.trim() || null
      });
      setSiteForm({ domain: "", list_type: "blacklist", note: "" });
      setShowSiteForm(false);
      await loadDashboard(session);
    } catch (err) {
      setSiteFormError("Could not add site. Try again.");
    }
  };

  const handleRemoveSite = async (id) => {
    await axios.delete(`${API}/caregiver/site-list/${id}`);
    setSiteList(prev => prev.filter(s => s.id !== id));
  };

  // ─── RENDER: Family dashboard (link-based pairing — the new system) ──────
  // Branded to match the HAVN Browser: ink-blue ground, light cards, ember
  // accents, large calm type.
  if (user && familyView.seniors && familyView.seniors.length > 0 && !session) {
    const B = {
      page: {
        minHeight: "100vh",
        background: "linear-gradient(180deg, #0d1b33 0%, #16233d 100%)",
        color: "#fff",
        padding: "0 0 60px",
      },
      header: {
        display: "flex",
        alignItems: "center",
        gap: 14,
        padding: "22px 28px",
        borderBottom: "1px solid rgba(255,255,255,0.08)",
      },
      orb: { width: 42, height: 42 },
      brand: { display: "flex", flexDirection: "column", lineHeight: 1.15 },
      brandTitle: { fontSize: 21, fontWeight: 700, letterSpacing: 0.2 },
      brandSub: { fontSize: 13, opacity: 0.6 },
      refresh: {
        marginLeft: "auto",
        background: "rgba(255,255,255,0.08)",
        border: "1px solid rgba(255,255,255,0.14)",
        color: "#fff",
        borderRadius: 12,
        padding: "10px 16px",
        fontSize: 15,
        cursor: "pointer",
      },
      wrap: { maxWidth: 1000, margin: "0 auto", padding: "26px 22px 0" },
      hello: { fontSize: 26, fontWeight: 600, margin: "0 0 4px" },
      helloSub: { fontSize: 15, opacity: 0.6, margin: "0 0 22px" },
      statsRow: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
        gap: 14,
        marginBottom: 26,
      },
      stat: {
        background: "#fff",
        borderRadius: 18,
        padding: "20px 18px",
        textAlign: "center",
        color: "#14294a",
      },
      statValue: { fontSize: 40, fontWeight: 700, lineHeight: 1.1 },
      statLabel: { fontSize: 14, opacity: 0.6, marginTop: 4 },
      sectionTitle: { fontSize: 19, fontWeight: 600, margin: "0 0 12px" },
      alert: {
        background: "#fff",
        color: "#14294a",
        borderRadius: 16,
        padding: "16px 18px",
        marginBottom: 12,
        display: "flex",
        alignItems: "center",
        gap: 14,
        flexWrap: "wrap",
      },
      badge: (bg, color) => ({
        background: bg,
        color,
        borderRadius: 999,
        padding: "6px 12px",
        fontSize: 13,
        fontWeight: 600,
        whiteSpace: "nowrap",
      }),
      calm: {
        background: "rgba(255,255,255,0.06)",
        border: "1px solid rgba(255,255,255,0.10)",
        borderRadius: 18,
        padding: "26px 20px",
        textAlign: "center",
        fontSize: 17,
        opacity: 0.85,
      },
      note: {
        display: "flex",
        alignItems: "center",
        gap: 8,
        fontSize: 13.5,
        opacity: 0.6,
        marginTop: 18,
      },
      cta: {
        marginTop: 20,
        background: "#ff5a2a",
        color: "#fff",
        border: "none",
        borderRadius: 14,
        padding: "16px 22px",
        fontSize: 17,
        fontWeight: 600,
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        gap: 10,
      },
      wordmark: { height: 22, opacity: 0.5, marginTop: 34 },
    };

    return (
      <div style={B.page}>
        <header style={B.header}>
          <img
            src="/orb_havn.png"
            alt=""
            style={B.orb}
            onError={(e) => { e.target.style.display = "none"; }}
          />
          <div style={B.brand}>
            <span style={B.brandTitle}>Family Dashboard</span>
            <span style={B.brandSub}>HAVN keeps watch — you stay in the loop</span>
          </div>
          <button style={B.refresh} onClick={loadFamily} data-testid="cp-family-refresh">
            <RefreshCw size={14} style={{ marginRight: 6, verticalAlign: "-2px" }} />
            Refresh
          </button>
        </header>

        <div style={B.wrap}>
          {familyView.seniors.map((s, idx) => {
            const t = s.today || {};
            const warnings = (t.caution_count || 0) + (t.danger_count || 0);
            const quiet = !t.alerts || t.alerts.length === 0;
            return (
              <div key={s.senior_id} style={{ marginBottom: 34 }}>
                <h2 style={B.hello}>
                  {familyView.seniors.length > 1
                    ? `Family member ${idx + 1}`
                    : "Your family member"}
                </h2>
                <p style={B.helloSub}>Today at a glance</p>

                <div style={B.statsRow}>
                  <div style={B.stat}>
                    <div style={{ ...B.statValue, color: "#2563eb" }}>{t.total_sites ?? 0}</div>
                    <div style={B.statLabel}>Sites checked</div>
                  </div>
                  <div style={B.stat}>
                    <div style={{ ...B.statValue, color: "#f59e0b" }}>{warnings}</div>
                    <div style={B.statLabel}>Warnings</div>
                  </div>
                  <div style={B.stat}>
                    <div style={{ ...B.statValue, color: "#ef4444" }}>{t.danger_count ?? 0}</div>
                    <div style={B.statLabel}>High risk</div>
                  </div>
                  <div style={B.stat}>
                    <div style={{ ...B.statValue, color: "#ff5a2a" }}>{t.override_count ?? 0}</div>
                    <div style={B.statLabel}>Warnings ignored</div>
                  </div>
                </div>

                {quiet ? (
                  <div style={B.calm}>
                    <CheckCircle size={22} style={{ color: "#4ade80", verticalAlign: "-4px", marginRight: 8 }} />
                    All quiet today — nothing needs your attention.
                  </div>
                ) : (
                  <>
                    <h3 style={B.sectionTitle}>Needs your attention</h3>
                    {t.alerts.map((evt, i) => {
                      const isOverride = evt.action === "override_continued";
                      const danger = evt.verdict === "DANGER";
                      return (
                        <div key={i} style={B.alert} data-testid={`cp-family-alert-${i}`}>
                          <span
                            style={B.badge(
                              danger ? "rgba(239,68,68,0.12)" : "rgba(245,158,11,0.14)",
                              danger ? "#dc2626" : "#b45309"
                            )}
                          >
                            {isOverride ? "Warning ignored" : danger ? "High risk" : "Caution"}
                          </span>
                          <div style={{ flex: 1, minWidth: 200 }}>
                            <div style={{ fontSize: 16, fontWeight: 600 }}>
                              {evt.category && evt.category !== "UNKNOWN" ? evt.category : "Risky site"}
                              {evt.danger_reason ? ` — ${evt.danger_reason}` : ""}
                            </div>
                            <div style={{ fontSize: 13.5, opacity: 0.6, marginTop: 2 }}>
                              {formatDate(evt.created_at)}
                            </div>
                          </div>
                          {isOverride && (
                            <span style={{ fontSize: 13.5, color: "#dc2626", fontWeight: 600 }}>
                              They continued anyway
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
            );
          })}

          <div style={B.note}>
            <CheckCircle size={14} />
            Privacy protected — severity and category only. Their private browsing stays private.
          </div>

          {pushState === "enabled" ? (
            <div style={{ ...B.note, color: "#4ade80", opacity: 1 }}>
              <CheckCircle size={14} />
              Alerts are on for this device — high-risk events will reach you.
            </div>
          ) : (
            <>
              <button style={B.cta} onClick={enablePush} data-testid="cp-enable-push">
                <Bell size={17} />
                Enable alerts on this device
              </button>
              <div style={B.note}>
                {pushState === "denied"
                  ? "Notifications are blocked for this site in your browser settings."
                  : pushState === "unsupported"
                  ? "This browser can't show notifications — on iPhone, add this page to your Home Screen first, then try again."
                  : pushState === "error"
                  ? `Couldn't enable alerts${pushError ? ` — ${pushError}` : " — try again in a moment."}`
                  : "Get notified of high-risk moments without keeping this page open."}
              </div>
            </>
          )}

          <div style={{ textAlign: "center" }}>
            <img
              src="/havn_wordmark_white.png"
              alt="HAVN"
              style={B.wordmark}
              onError={(e) => { e.target.style.display = "none"; }}
            />
            <div style={{ fontSize: 12.5, opacity: 0.4, marginTop: 6 }}>
              Signed in as {user.email}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─── RENDER: Lock screen ─────────────────────────────────────────────────
  if (!session) {
    return (
      <div className="cp-lockscreen">
        <div className="cp-lockscreen-card">
          <div className="cp-lockscreen-icon"><Shield size={36} /></div>
          <h1 className="cp-lockscreen-title">HAVN Caregiver Portal</h1>
          {user && familyView.loading && (
            <p className="cp-lockscreen-subtitle">Checking your family links…</p>
          )}
          {user && familyView.seniors && familyView.seniors.length === 0 && (
            <p className="cp-lockscreen-subtitle" style={{ color: "#f59e0b" }}>
              Signed in as {user.email} — no family linked yet. Accept an invitation
              link from your family member's HAVN Browser, or use an access code below.
              {familyView.error && (
                <>
                  <br />
                  <span style={{ fontSize: 12, opacity: 0.8 }}>
                    (server said: {familyView.error}
                    {familyView.error === "auth_required" || familyView.error === "invalid_token"
                      ? " — your sign-in token wasn't accepted"
                      : familyView.error === "supabase_unavailable"
                      ? " — the backend has no Supabase credentials"
                      : familyView.error === "query_failed"
                      ? " — the database query failed"
                      : ""}
                    )
                  </span>
                </>
              )}
            </p>
          )}
          <p className="cp-lockscreen-subtitle">
            Enter the 6-character access code shown in your person's Contacts panel.
          </p>
          <input
            className="cp-code-input"
            type="text"
            maxLength={6}
            placeholder="ABC123"
            value={codeInput}
            onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
            onKeyDown={(e) => e.key === "Enter" && handleAuth()}
            autoFocus
            data-testid="cp-code-input"
          />
          {authError && <p className="cp-auth-error">{authError}</p>}
          <button
            className="cp-auth-btn"
            onClick={handleAuth}
            disabled={isAuthenticating || codeInput.length < 4}
            data-testid="cp-auth-btn"
          >
            {isAuthenticating ? "Verifying..." : "Access Portal"}
          </button>
          <p className="cp-lockscreen-note">
            Privacy protected — you will see activity summaries only, not conversation content.
          </p>
        </div>
      </div>
    );
  }

  // ─── RENDER: Dashboard ────────────────────────────────────────────────────
  const whitelist = siteList.filter(s => s.list_type === "whitelist");
  const blacklist = siteList.filter(s => s.list_type === "blacklist");

  return (
    <div className="cp-portal">
      {/* Header */}
      <header className="cp-header">
        <div className="cp-header-left">
          <Shield size={20} className="cp-header-icon" />
          <span className="cp-header-title">Caregiver Portal</span>
        </div>
        <button
          className="cp-signout"
          onClick={() => { sessionStorage.removeItem("cp_session"); setSession(null); }}
          data-testid="cp-signout"
        >
          Sign out
        </button>
      </header>

      {/* Nav tabs */}
      <nav className="cp-tabs">
        {[
          { id: "overview", label: "Overview", icon: BarChart2 },
          { id: "reminders", label: "Reminders", icon: Bell },
          { id: "sites", label: "Site Lists", icon: Globe },
          { id: "activity", label: "Activity", icon: List }
        ].map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            className={`cp-tab ${activeTab === id ? "active" : ""}`}
            onClick={() => setActiveTab(id)}
            data-testid={`cp-tab-${id}`}
          >
            <Icon size={16} />
            <span>{label}</span>
          </button>
        ))}
      </nav>

      {isLoading && <div className="cp-loading">Loading...</div>}

      {/* ── OVERVIEW TAB ─────────────────────────────────────────────── */}
      {activeTab === "overview" && dashboard && (
        <div className="cp-content">
          <div className="cp-section-title">This week</div>
          <div className="cp-stats-row">
            <StatCard label="Sessions" value={dashboard.week_sessions} color="#60a5fa" icon={Clock} />
            <StatCard label="Risk events" value={dashboard.week_risk_events} color="#f59e0b" icon={AlertTriangle} />
            <StatCard label="High risk" value={dashboard.week_danger_events} color="#ef4444" icon={Shield} />
          </div>
          <div className="cp-last-active">
            Last active: <strong>{formatDate(dashboard.last_active)}</strong>
          </div>
          <div className="cp-section-title" style={{ marginTop: 20 }}>4-week trend</div>
          <WeeklyChart data={dashboard.chart_data} />
          <div className="cp-privacy-note">
            <CheckCircle size={13} />
            Privacy protected — showing counts only. No conversation content or URLs.
          </div>
        </div>
      )}

      {/* ── REMINDERS TAB ────────────────────────────────────────────── */}
      {activeTab === "reminders" && (
        <div className="cp-content">
          <div className="cp-section-header">
            <span className="cp-section-title">Reminders & nudges</span>
            <button className="cp-add-btn" onClick={() => setShowReminderForm(true)} data-testid="cp-add-reminder">
              <Plus size={16} /> Add
            </button>
          </div>

          {showReminderForm && (
            <div className="cp-form-card" data-testid="cp-reminder-form">
              <input className="cp-input" placeholder="Title (e.g. Take your meds)" value={reminderForm.title}
                onChange={e => setReminderForm(p => ({ ...p, title: e.target.value }))} />
              <textarea className="cp-input cp-textarea" placeholder="Message to show/speak to user"
                value={reminderForm.body} rows={2}
                onChange={e => setReminderForm(p => ({ ...p, body: e.target.value }))} />
              <select className="cp-input" value={reminderForm.repeat}
                onChange={e => setReminderForm(p => ({ ...p, repeat: e.target.value }))}>
                <option value="none">One-time nudge</option>
                <option value="daily">Daily reminder</option>
                <option value="weekly">Weekly reminder</option>
              </select>
              {(reminderForm.repeat === "daily" || reminderForm.repeat === "weekly") && (
                <input type="time" className="cp-input" value={reminderForm.scheduled_time}
                  onChange={e => setReminderForm(p => ({ ...p, scheduled_time: e.target.value }))} />
              )}
              {reminderForm.repeat === "weekly" && (
                <select className="cp-input" value={reminderForm.scheduled_weekday}
                  onChange={e => setReminderForm(p => ({ ...p, scheduled_weekday: e.target.value }))}>
                  <option value="">Day of week</option>
                  {["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"].map((d,i) => (
                    <option key={i} value={i}>{d}</option>
                  ))}
                </select>
              )}
              <div className="cp-form-btns">
                <button className="cp-form-cancel" onClick={() => setShowReminderForm(false)}>Cancel</button>
                <button className="cp-form-save" onClick={handleCreateReminder} data-testid="cp-save-reminder">
                  Save reminder
                </button>
              </div>
            </div>
          )}

          {reminders.length === 0 && !showReminderForm && (
            <div className="cp-empty">No reminders yet. Add one above.</div>
          )}
          {reminders.map(r => (
            <div key={r.id} className="cp-reminder-card" data-testid={`cp-reminder-${r.id}`}>
              <div className="cp-reminder-header">
                <Bell size={14} className="cp-reminder-icon" />
                <span className="cp-reminder-title">{r.title}</span>
                <button className="cp-icon-btn" onClick={() => handleDeleteReminder(r.id)}>
                  <Trash2 size={14} />
                </button>
              </div>
              <p className="cp-reminder-body">{r.body}</p>
              <span className="cp-reminder-meta">
                {r.repeat === "none" ? "One-time" : r.repeat === "daily" ? `Daily${r.scheduled_time ? " at " + r.scheduled_time : ""}` : `Weekly${r.scheduled_time ? " at " + r.scheduled_time : ""}`}
                {r.acknowledged_at && <> · Seen {formatDate(r.acknowledged_at)}</>}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* ── SITE LISTS TAB ───────────────────────────────────────────── */}
      {activeTab === "sites" && (
        <div className="cp-content">
          <div className="cp-section-header">
            <span className="cp-section-title">Site lists</span>
            <button className="cp-add-btn" onClick={() => setShowSiteForm(true)} data-testid="cp-add-site">
              <Plus size={16} /> Add
            </button>
          </div>

          {showSiteForm && (
            <div className="cp-form-card" data-testid="cp-site-form">
              <input className="cp-input" placeholder="Domain (e.g. amazon.com)"
                value={siteForm.domain}
                onChange={e => setSiteForm(p => ({ ...p, domain: e.target.value }))} />
              <select className="cp-input" value={siteForm.list_type}
                onChange={e => setSiteForm(p => ({ ...p, list_type: e.target.value }))}>
                <option value="blacklist">Blacklist — warn user</option>
                <option value="whitelist">Whitelist — recommend to user</option>
              </select>
              <input className="cp-input" placeholder="Note (e.g. Safe banking site / Watch out — casino)"
                value={siteForm.note}
                onChange={e => setSiteForm(p => ({ ...p, note: e.target.value }))} />
              {siteFormError && <p className="cp-form-error">{siteFormError}</p>}
              <div className="cp-form-btns">
                <button className="cp-form-cancel" onClick={() => { setShowSiteForm(false); setSiteFormError(""); }}>Cancel</button>
                <button className="cp-form-save" onClick={handleAddSite} data-testid="cp-save-site">Save</button>
              </div>
            </div>
          )}

          {blacklist.length > 0 && (
            <>
              <div className="cp-list-label cp-list-label--black">Flagged sites ({blacklist.length})</div>
              {blacklist.map(s => (
                <div key={s.id} className="cp-site-card cp-site-card--black" data-testid={`cp-site-${s.id}`}>
                  <span className="cp-site-domain">{s.domain}</span>
                  {s.note && <span className="cp-site-note">{s.note}</span>}
                  <button className="cp-icon-btn" onClick={() => handleRemoveSite(s.id)}><Trash2 size={14} /></button>
                </div>
              ))}
            </>
          )}
          {whitelist.length > 0 && (
            <>
              <div className="cp-list-label cp-list-label--white">Recommended sites ({whitelist.length})</div>
              {whitelist.map(s => (
                <div key={s.id} className="cp-site-card cp-site-card--white" data-testid={`cp-site-${s.id}`}>
                  <span className="cp-site-domain">{s.domain}</span>
                  {s.note && <span className="cp-site-note">{s.note}</span>}
                  <button className="cp-icon-btn" onClick={() => handleRemoveSite(s.id)}><Trash2 size={14} /></button>
                </div>
              ))}
            </>
          )}
          {siteList.length === 0 && !showSiteForm && (
            <div className="cp-empty">No sites added yet.</div>
          )}
        </div>
      )}

      {/* ── ACTIVITY TAB ─────────────────────────────────────────────── */}
      {activeTab === "activity" && dashboard && (
        <div className="cp-content">
          <div className="cp-section-title">Recent risk events</div>
          <div className="cp-privacy-note" style={{ marginBottom: 12 }}>
            <CheckCircle size={13} />
            Showing event category and severity only — no URLs or content.
          </div>
          {(!dashboard.incident_feed || dashboard.incident_feed.length === 0) ? (
            <div className="cp-empty">No risk events this week.</div>
          ) : (
            dashboard.incident_feed.map((evt, i) => {
              const style = RISK_COLORS[evt.risk_level] || RISK_COLORS.CAUTION_HARD;
              return (
                <div key={i} className="cp-incident-card" data-testid={`cp-incident-${i}`}>
                  <div className="cp-incident-badge" style={{ background: style.bg, color: style.color }}>
                    {style.label}
                  </div>
                  <div className="cp-incident-info">
                    <span className="cp-incident-category">{CATEGORY_LABELS[evt.category] || evt.category}</span>
                    <span className="cp-incident-time">{formatDate(evt.timestamp)}</span>
                  </div>
                  {evt.user_proceeded && (
                    <span className="cp-incident-proceeded">User proceeded despite warning</span>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
